import { Box, Button, FormControlLabel, Link, Stack } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { Checkbox, Drawer } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ImageAdd01Icon,
  Comment01Icon,
  MoneySend01Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import Comments from "components/common/comments/Comments";
import { CommentActions } from "hooks/useComments";
import ContactType from "../../../enums/ContactType";
import { ParentType } from "../../../enums/Comments";
import { ContactRouterDrawerType } from "../../../enums/ContactRouterDrawerType";
import { useFeatureEntitlements } from "@frontegg/react";
import {
  ContactLoadingStates,
  FormikContact,
} from "../../../hooks/useContactData";
import { ATTACHMENT_PARENT_TYPE } from "enums/AttachmentParentType";
import { LocalImageAsset } from "hooks/useAttachmentAndAssetHandling";
import { AttachmentDrawerType } from "enums/AttachmentDrawerType";
import AttachmentsComponent, {
  AttachmentRenderType,
} from "components/common/attachments/AttachmentComponent";
import Summary from "../../common/Summary";
import RenderRelationship from "../../common/contacts/RenderRelationship";
import { FormikProps } from "formik";
import LatestPayments from "../payments/LatestPayments";
import useCustomFieldRenderer from "hooks/useCustomFieldRenderer";
import CustomFieldParentType from "enums/CustomFieldParentType";
import { formatAddressDisplay } from "hooks/useAddressData";
import CustomPanel from "components/common/CustomPanel";
import { Accordion } from "@outpave/ds";
import { fullName } from "utils/fullName";

interface ContactDetailsDrawerProps {
  open: boolean;
  drawerType: ContactRouterDrawerType;
  formikContact: FormikProps<FormikContact>;
  specificLoadingStates: ContactLoadingStates;
  onCancel: () => void;
  onSave: () => void;
  onEdit?: () => void;
  onInvite?: () => void;
  commentActions?: CommentActions;
  /** Fires once the drawer has slid out. */
  onAfterClose?: () => void;
}

const ContactDetailsDrawer = (props: ContactDetailsDrawerProps) => {
  const {
    open,
    drawerType,
    formikContact,
    specificLoadingStates,
    onCancel,
    onSave,
    onEdit,
    onInvite,
    commentActions,
    onAfterClose,
  } = props;
  const { t } = useTranslation();
  const { isEntitled: featurePayments } =
    useFeatureEntitlements("feature_payments");

  const contactData = formikContact?.values;
  const contactId = contactData?.id || "";

  function getLocalPaymentBankInfo(paymentDetails: any) {
    if (
      paymentDetails &&
      paymentDetails.accountName &&
      paymentDetails.accountNumber &&
      paymentDetails.accountNumber.length >= 4
    ) {
      const last4 = paymentDetails.accountNumber.slice(-4);
      return `${paymentDetails.accountName} *** ${last4}`;
    }
    return "";
  }

  const { filteredCustomFields } = useCustomFieldRenderer({
    parentType: CustomFieldParentType.CONTACT,
    customFields: formikContact.values.customFields,
  });

  const summaryItems = [
    {
      title: t("Name"),
      value: fullName(contactData),
      shouldShow: contactData.contactType === ContactType.Individual,
    },
    {
      title: t("Business Name"),
      value: contactData.businessName,
      shouldShow: true,
    },
    {
      title: t("Website"),
      value: contactData.website ? (
        <Link
          href={
            contactData.website.startsWith("http")
              ? contactData.website
              : `http://${contactData.website}`
          }
          target="_blank"
          rel="noopener noreferrer"
        >
          {contactData.website}
        </Link>
      ) : null,
      fullWidth: true,
      shouldShow: contactData.contactType === ContactType.Business,
    },
    {
      title: t("Email"),
      fullWidth: true,
      value: contactData.email,
      shouldShow: true,
    },
    {
      title: t("Phone"),
      value: contactData.phone,
      shouldShow: true,
    },
    {
      title: t("Address"),
      fullWidth: true,
      value: formatAddressDisplay(contactData.address),
      shouldShow: true,
    },
    {
      title: t("Relationship Type"),
      value: <RenderRelationship relationship={contactData.relationshipType} />,
      shouldShow: true,
    },
    {
      title: t("Bank Information"),
      fullWidth: true,
      value:
        contactData.paymentBankInfo ||
        getLocalPaymentBankInfo(contactData.paymentDetails),
      shouldShow:
        (contactData.paymentBankInfo ||
          getLocalPaymentBankInfo(contactData.paymentDetails)) &&
        featurePayments,
    },
    ...(filteredCustomFields?.length > 0
      ? filteredCustomFields.map((field) => ({
          title: field.label,
          value: field.value,
          shouldShow: true,
        }))
      : []),
  ].filter((field) => field.shouldShow);

  const handleAddAssets = (newAssets: LocalImageAsset[]) => {
    formikContact.setFieldValue("attachments", [
      ...(formikContact.values.attachments ?? []),
      ...newAssets,
    ]);
  };

  const handleRemoveAsset = (assetId: string) => {
    formikContact.setFieldValue(
      "attachments",
      (formikContact.values.attachments ?? []).filter(
        (asset) => asset.id !== assetId,
      ),
    );
  };

  const isLoading = specificLoadingStates.loadingContact && !contactId;
  const isIndividualWithoutBusiness =
    formikContact.values.contactType === ContactType.Individual &&
    !formikContact.values.parentContactId;

  const busy =
    isLoading ||
    specificLoadingStates.creatingContact ||
    !!commentActions?.addingComment;

  return (
    <>
      <Drawer
        open={open}
        onClose={() => onCancel()}
        onAfterClose={onAfterClose}
        title={
          drawerType === ContactRouterDrawerType.NewContact
            ? t("Review Contact")
            : t("Contact Details")
        }
        loading={busy}
        footer={
          <>
            <Button variant="primary-outlined" onClick={() => onCancel()}>
              {drawerType === ContactRouterDrawerType.NewContact
                ? t("Back")
                : t("Exit")}
            </Button>

            {drawerType !== ContactRouterDrawerType.NewContact &&
              formikContact.values.contactType !== ContactType.Business && (
                <Button
                  variant="secondary-filled"
                  onClick={onInvite}
                  disabled={specificLoadingStates.invitingContact}
                >
                  {t("Invite")}
                </Button>
              )}

            {drawerType === ContactRouterDrawerType.NewContact ? (
              <Button
                variant="primary-filled"
                onClick={onSave}
                disabled={
                  specificLoadingStates.creatingContact ||
                  commentActions?.addingComment
                }
              >
                {t("Save")}
              </Button>
            ) : (
              <Button variant="primary-filled" onClick={onEdit}>
                {t("Edit")}
              </Button>
            )}
          </>
        }
      >
        <Summary
          disableContainer
          items={summaryItems}
          loading={isLoading}
          showLoadingIcon={true}
        />

        <Stack>
          <Accordion
            title={t("Attachments")}
            icon={<HugeiconsIcon icon={ImageAdd01Icon} size={16} />}
          >
            <AttachmentsComponent
              renderType={AttachmentRenderType.ACCORDION}
              drawerType={AttachmentDrawerType.ATTACHMENT}
              parentDetails={{
                parentId: contactId || "",
                parentType: ATTACHMENT_PARENT_TYPE.CONTACT,
              }}
              // A saved contact's files save as they are added, so it shows the saved list.
              localAssets={
                contactId ? undefined : formikContact.values.attachments
              }
              onAddAssets={handleAddAssets}
              onRemoveAssets={handleRemoveAsset}
            />
          </Accordion>

          {drawerType !== ContactRouterDrawerType.NewContact && contactId && (
            <Accordion
              title={t("Comments")}
              icon={<HugeiconsIcon icon={Comment01Icon} size={16} />}
            >
              <Comments parentId={contactId} parentType={ParentType.CONTACT} />
            </Accordion>
          )}

          {drawerType === ContactRouterDrawerType.NewContact && contactData && (
            <Accordion
              title={t("Comments")}
              icon={<HugeiconsIcon icon={Comment01Icon} size={16} />}
            >
              <Comments
                commentActions={commentActions}
                parentId=""
                parentType={ParentType.CONTACT}
                rows={4}
                placeholder={t("AddCommentWithMention")}
              />
            </Accordion>
          )}

          {drawerType !== ContactRouterDrawerType.NewContact && contactId && (
            <Accordion
              title={t("Recent Payments")}
              icon={<HugeiconsIcon icon={MoneySend01Icon} size={16} />}
            >
              <LatestPayments contactId={contactId} />
            </Accordion>
          )}
        </Stack>

        {drawerType === ContactRouterDrawerType.NewContact &&
          isIndividualWithoutBusiness && (
            <CustomPanel>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formikContact.values.inviteContact || false}
                    onChange={(e) =>
                      formikContact.setFieldValue(
                        "inviteContact",
                        e.target.checked,
                      )
                    }
                    color="primary"
                  />
                }
                label={
                  <Trans
                    i18nKey="InviteContactDescription"
                    components={{
                      contact_email: (
                        <Box
                          component="span"
                          sx={{ color: "var(--color-text-brand)" }}
                        />
                      ),
                    }}
                    values={{ email: formikContact.values.email }}
                  />
                }
                sx={{ gap: 2, padding: 2 }}
              />
            </CustomPanel>
          )}
      </Drawer>
    </>
  );
};

export default ContactDetailsDrawer;
